import {
  ForgotPasswordModel,
  LoginModel,
  RegisterUserModel,
  ResetPasswordModel,
  TokenResponseModel,
  ValidateModel,
  EmailVerificationModel,
  ResendVerificationCodeModel
} from '../models/auth.model';
import {Config} from '../config';
import {Storage} from '@ionic/storage';
import {Router} from '@angular/router';
import {Injectable} from '@angular/core';
import {HttpService} from './http.service';
import {Observable, throwError, of} from 'rxjs';
import {TranslateService} from '@ngx-translate/core';
import {TokenStoreService} from './token-store.service';
import {ErrorHandlerService} from './error-handler.service';
import {HttpBackend, HttpClient} from '@angular/common/http';
import {AlertController, LoadingController} from '@ionic/angular';
import {UserModel, MessageResponseModel} from 'src/app/models/user.model';
import {finalize, publishReplay, refCount, switchMap, tap, catchError, map} from 'rxjs/operators';

@Injectable()
export class AuthService extends HttpService {
  private refreshing: Observable<TokenResponseModel>;
  private alert;

  constructor(
    http: HttpClient,
    private router: Router,
    private storage: Storage,
    private handler: HttpBackend,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private loadingCtrl: LoadingController,
    private tokenStoreService: TokenStoreService,
    private errorHandlerService: ErrorHandlerService,
  ) {
    super(http);
  }

  get loggedIn() {
    return this.tokenStoreService.loggedIn;
  }

  get onLoginChange() {
    return this.tokenStoreService.onLoginChange;
  }

  get onTokenChange() {
    return this.tokenStoreService.onTokenChange;
  }

  get token() {
    return this.tokenStoreService.token;
  }

  forgot(request: ForgotPasswordModel) {
    const http = new HttpClient(this.handler);
    return http.post<MessageResponseModel>(`${Config.apiUrl}/auth/forgot`, request, {
      headers: {
        'X-locale': 'nl',
        'X-App-Version': Config.appVersion
      }
    });
  }

  login(credentials: LoginModel) {
    const http = new HttpClient(this.handler);
    return http.post<TokenResponseModel>(`${Config.apiUrl}/auth/login`, credentials, {
      headers: {
        'X-App-Version': Config.appVersion
      }
    }).pipe(
      switchMap(token => this.tokenStoreService.updateToken(token))
    );
  }

  logout() {
    return this.token.pipe(
      switchMap((token) => {
        if (token) {
          return this.http.post(`${Config.apiUrl}/user/logout`, null).pipe(
            catchError(() => of()),
            switchMap(() => this.tokenStoreService.updateToken(null)),
            map(() => true)
          );
        }

        return of(true);
      })
    );
  }

  register(request: RegisterUserModel) {
    return this.http.post<TokenResponseModel>(`${Config.apiUrl}/auth/register`, request, {
      headers: {
        'X-App-Version': Config.appVersion
      }
    });
  }

  refresh(currentToken: string): Observable<TokenResponseModel> {
    if (!currentToken) {
      return throwError('unauthorized');
    }

    if (!this.refreshing) {
      this.refreshing = this.http.post<TokenResponseModel>(`${Config.apiUrl}/auth/refresh`, null, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
          'X-App-Version': Config.appVersion
        }
      }).pipe(
        switchMap(newToken => this.tokenStoreService.updateToken(newToken).then(() => newToken)),
        tap(() => {
          this.refreshing = null;
        }),
        publishReplay(1),
        refCount()
      );
    }

    return this.refreshing;
  }

  reset(request: ResetPasswordModel) {
    const http = new HttpClient(this.handler);
    return http.post<TokenResponseModel>(`${Config.apiUrl}/auth/reset-password`, request, {
      headers: {
        'X-App-Version': Config.appVersion
      }
    });
  }

  emailVerification(request: EmailVerificationModel) {
    const http = new HttpClient(this.handler);
    return http.post(`${Config.apiUrl}/auth/verify`, request, {
      headers: {
        'X-App-Version': Config.appVersion
      }
    });
  }

  resendVerificationCode(request: ResendVerificationCodeModel) {
    const http = new HttpClient(this.handler);
    return http.post(`${Config.apiUrl}/auth/verify/resend`, request, {
      headers: {
        'X-locale': 'nl',
        'X-App-Version': Config.appVersion
      }
    });
  }

  updateProfile(request: UserModel) {
    return this.http.post<UserModel>(`${Config.apiUrl}/user/me`, request);
  }

  validate(request: ValidateModel) {
    return this.http.post<TokenResponseModel>(`${Config.apiUrl}/auth/validate`, request).pipe(
      switchMap(token => this.tokenStoreService.updateToken(token))
    );
  }

  refreshToken(request: string) {
    const userName = {user_name: request};
    if (!this.refreshing) {
      this.refreshing = this.http.post<TokenResponseModel>(`${Config.apiUrl}/auth/login`, userName)
        .pipe(
          switchMap(newToken => this.tokenStoreService.updateToken(newToken).then(() => newToken)),
          tap(() => {
            this.refreshing = null;
          }),
          publishReplay(1),
          refCount()
        );
    }
    return this.refreshing;
  }

  showLogin() {
    return this.translate.get([
      'Login.Title',
      'Login.Email',
      'Login.Password',
      'Buttons.Login',
      'Buttons.Cancel'
    ]).pipe(
      switchMap(messages => new Observable<boolean>(observer => {
        this.storage.get('login_email').catch(() => null).then(email => {
          this.loginAlert(messages, observer, email);
        });
        return () => {
          this.alert.dismiss();
        };
      }))
    );
  }


  async loginAlert(messages, observer, email) {
    this.alert = await this.alertCtrl.create({
      header: messages['Login.Title'],
      backdropDismiss: true,
      inputs: [
        {
          name: 'email',
          type: 'email',
          placeholder: messages['Login.Email'],
          value: email || null,
        },
        {
          name: 'password',
          type: 'password',
          placeholder: messages['Login.Password'],
        }
      ],
      buttons: [
        {
          text: messages['Buttons.Cancel'],
          role: 'cancel',
          handler: () => {
            observer.next(false);
            observer.complete();

            return false;
          }
        },
        {
          text: messages['Buttons.Login'],
          handler: data => {
            this.translate.get(['Loading']).subscribe(translatedMessages => {
              this.loadingCtrl.create({
                message: translatedMessages['Loading'],
              }).then(loader => {
                loader.present();
              });
            });
            this.login(data).pipe(
              finalize(() => this.loadingCtrl.dismiss()),
            ).subscribe(
              () => {
                this.storage.set('login_email', data.email);

                observer.next(true);
                observer.complete();
              },
              error => this.errorHandlerService.handle(error)
            );

            return false;
          }
        }
      ]
    });
    this.alert.present();
  }
}
